<script setup>
/**
 * 上映スケジュールAI ステップ3：タイムチャート作成・表示・編集（DEV-14983）
 *
 * - 対象期間（開始日〜終了日、最大 14 日）を選び、条件（CM・予告・清掃間隔、作品ごとの優先度・目標回数・要望）を入力
 * - 保存済みの期間と重なる期間も選べる（カレンダーで排班済みの日を強調し、選んだときに警告する）。
 *   重なる日の上映回は AI 生成・タイムチャート保存の際に確認のうえ上書きする。保存済みの期間は削除できる
 * - ①条件設定 → ②ルール確認 → ③タイムチャート をステッパーで進める。①を保存するまで②③には進めない
 * - 対象期間の選択は①の「期間の設定」に置き、②③では表示中の期間を文字だけで出す
 * - ②に入ると未保存の入力を保存し、要望が未解析（rulesStale）なら AI で自動解析してプレビュー表示（読み取り専用）
 * - ②「ルールを確定」でルール保存 → AI 生成（非同期ジョブを 3 秒ごとにポーリング）→ ③で確認・ドラッグ編集 → 保存
 *   再生成も②の「ルールを確定」から行う（専用の「AI生成」ボタンは置かない）
 * - 違反がある場合は確認のうえ「非準拠の下書き」として保存できる
 * 編集は dp:scheduleAi:plan:edit 権限が必要（なければ読み取り専用）。AI 案は下書きで公開されない。
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { createPlanState, providePlanState } from "./use-plan";
import {
  defaultPeriod,
  isDistributorUndeterminedCheck,
  isValidPeriod,
  periodFromQuery,
  periodRangeLabel,
} from "./utils";
import { getScheduleAiPlanPeriodsService } from "@/api/dp/index.js";
import PeriodPicker from "./components/period-picker.vue";
import WeekInput from "./components/week-input.vue";
import RulePanel from "./components/rule-panel.vue";
import TimeChart from "./components/time-chart.vue";
import ShowDialog from "./components/show-dialog.vue";
import CheckPanel from "./components/check-panel.vue";

// 前提となるステップ1・2 のメニュー path（動的ルートのため実際の path は登録済みルートから探す）
const SETTING_ROUTE_PATH = "scheduleAiSetting";
const MOVIE_ROUTE_PATH = "scheduleAiMovie";
// 生成中の経過秒数の更新間隔（ミリ秒）
const ELAPSED_INTERVAL = 1000;

const route = useRoute();
const router = useRouter();
const state = createPlanState();
providePlanState(state);

const {
  canEdit, dateFrom, dateTo, periodKey, days, loading, loadErrorMsg, check, shows, violations, rulesStale, textsDirty, rulesDirty,
  rules, anyDirty, showsDirty, showsSaving, generating, genError, genStartedAt, movies, rulesParsing, inputSaving,
  hasSavedShows, saved, inputDirty, generatedTime, planVersion, overlaps, overlapMessage, splitOverlaps, deleting, ruleMap,
  // エラーの原因になった前提ステップ（無関係なエラーではリンクを出さない）
  loadErrorStep, genErrorStep,
} = state;

const activeTab = ref("input");
const activeDay = ref("");
const highlightKey = ref("");

// ==================== 前提ステップへのリンク ====================

/**
 * メニュー path の末尾一致で登録済みルートを探す（権限がなければ null）
 */
const findRoutePath = (path) => router.getRoutes().find((r) => r.path.endsWith(`/${path}`))?.path || null;
const settingPath = computed(() => findRoutePath(SETTING_ROUTE_PATH));
const moviePath = computed(() => findRoutePath(MOVIE_ROUTE_PATH));

// ==================== 期間の選択 ====================

// 保存済みの期間一覧（期間選択のドロップダウン用）
const periods = ref([]);

/**
 * 保存済みの期間一覧を取得（失敗しても画面は使えるので空のままにする）
 */
const fetchPeriods = async () => {
  try {
    const res = await getScheduleAiPlanPeriodsService();
    periods.value = Array.isArray(res?.data) ? res.data : [];
  } catch {
    // エラーメッセージは request.js でトースト済み
  }
};

// 保存・削除で期間の一覧（準拠判定を含む）が変わるので取り直す
watch([saved, planVersion], (value, oldValue) => {
  if (oldValue && (value[0] !== oldValue[0] || value[1] !== oldValue[1])) fetchPeriods();
});

/**
 * 未保存の変更があれば破棄してよいか確認する
 */
const confirmDiscard = async (message) => {
  if (!anyDirty.value) return true;
  try {
    await ElMessageBox.confirm(message, "確認", {
      confirmButtonText: "破棄して移動",
      cancelButtonText: "キャンセル",
      type: "warning",
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * 期間を切り替える（期間選択・保存済み期間・重なっている期間から）
 */
const switchPeriod = async ({ dateFrom: from, dateTo: to }) => {
  if (!isValidPeriod(from, to) || (from === dateFrom.value && to === dateTo.value)) return;
  if (!(await confirmDiscard("保存されていない変更があります。破棄して別の期間に移動しますか？"))) return;
  highlightKey.value = "";
  // 再読み込みで同じ期間を開けるよう URL にも残す（旧形式の weekStart は消す）
  const query = { ...route.query, dateFrom: from, dateTo: to };
  delete query.weekStart;
  router.replace({ query });
  await state.changePeriod(from, to);
  activeDay.value = days.value[0] || "";
  activeTab.value = shows.value.length ? "chart" : "input";
  // 排班済みの日を含む期間を選んだら、上書きされることを知らせる
  // （既存期間の途中だけを上書きする期間は保存・作成できないのでエラーで知らせる）
  if (overlaps.value.length) {
    const notify = splitOverlaps.value.length ? ElMessage.error : ElMessage.warning;
    notify({ message: overlapMessage.value, duration: 6000 });
  }
};

/**
 * 保存済みの期間を削除（条件設定・ルール・上映回もすべて削除）
 */
const handleDeletePeriod = async () => {
  try {
    await ElMessageBox.confirm(
      `${periodRangeLabel(dateFrom.value, dateTo.value)} のタイムチャート（条件設定・ルール・上映回）をすべて削除します。元に戻せません。削除しますか？`,
      "確認",
      { confirmButtonText: "削除", cancelButtonText: "キャンセル", type: "warning", confirmButtonClass: "el-button--danger" },
    );
  } catch {
    return;
  }
  highlightKey.value = "";
  if (await state.deletePeriod()) {
    activeDay.value = days.value[0] || "";
    // 期間一覧は saved の変化を見て取り直される
    activeTab.value = "input";
  }
};

// ==================== 状態表示 ====================

// 上映回が 1 件もなければ判定せず「未作成」と表示する（空のチャートを「実行可能」と見せない）
const compliance = computed(() => (shows.value.length ? check.value?.compliance || null : null));
// 実行可能（COMPLIANT）は必須条件を満たしただけなので、配給会社の必須外の要望で判定できなかったものは別に「要確認」として出す
const distributorPending = computed(() => (check.value?.ruleChecks || [])
  .filter((c) => isDistributorUndeterminedCheck(c, c.hard ?? ruleMap.value.get(c.ruleId)?.hard))
  .map((c, i) => ({
    key: `dp-${c.ruleId ?? i}`,
    title: c.description || ruleMap.value.get(c.ruleId)?.description || `ルール ${c.ruleId ?? ""}`,
    reason: c.reason || "",
  })));
const needsParse = computed(() => rulesStale.value || textsDirty.value);
const ambiguousCount = computed(() => rules.value.filter((r) => r.resolve === "AMBIGUOUS").length);
// ルール確認タブの注意マーク
const ruleTabAlert = computed(() => needsParse.value || rulesDirty.value || ambiguousCount.value > 0);

// ==================== ステッパー ====================

// ①の保存前に②③を開こうとしたときの案内
const STEP1_REQUIRED_MSG = "先に「① 条件設定」で期間の設定を保存してください";

/**
 * 各ステップの状態
 * - done：完了（①＝保存済みで未保存の変更なし、②＝ルール確定済みで再解析不要、③＝AI生成済み or 上映回保存済み）
 * - locked：①が一度も保存されていないため進めない（②③）
 * note はステップ名の下に出す短い状態表示、alert は対応が必要な状態（赤字）
 */
const steps = computed(() => {
  const step1Saved = saved.value;
  const step1Done = step1Saved && !inputDirty.value;
  const step2Done = step1Saved && !needsParse.value && !rulesDirty.value;
  const step3Done = !!generatedTime.value || hasSavedShows.value;

  let step1Note = "未保存";
  if (step1Done) step1Note = "保存済み";
  else if (step1Saved) step1Note = "未保存の変更あり";

  let step2Note = "確定済み";
  if (!step1Saved) step2Note = "①の保存後に確認";
  else if (rulesParsing.value) step2Note = "解析中…";
  else if (needsParse.value || rulesDirty.value) step2Note = "未確定";
  else if (ambiguousCount.value) step2Note = `確定済み（要確認 ${ambiguousCount.value}件）`;

  let step3Note = "未作成";
  if (!step1Saved) step3Note = "①の保存後に作成";
  else if (generating.value) step3Note = "AI作成中…";
  else if (violations.value.length) step3Note = `違反 ${violations.value.length}件`;
  else if (step3Done) step3Note = "作成済み";

  return [
    { name: "input", no: 1, title: "条件設定", done: step1Done, locked: false, note: step1Note, alert: step1Saved && !step1Done },
    { name: "rules", no: 2, title: "ルール確認", done: step2Done, locked: !step1Saved, note: step2Note, alert: step1Saved && ruleTabAlert.value },
    { name: "chart", no: 3, title: "タイムチャート", done: step3Done, locked: !step1Saved, note: step3Note, alert: violations.value.length > 0 },
  ];
});

/**
 * ステップを切り替える。①未保存のときは②③に進めず案内を出す
 */
const goStep = (step) => {
  if (step.locked) {
    ElMessage.warning(STEP1_REQUIRED_MSG);
    return;
  }
  activeTab.value = step.name;
};

// 期間を切り替えて①が未保存の状態になったら①に戻す（②③を開いたままにしない）
watch(saved, (value) => {
  if (!value && activeTab.value !== "input") activeTab.value = "input";
});

/**
 * ②ルール確認に入ったときの処理
 * - ①に未保存の変更があれば先に保存（失敗したら①に戻す）
 * - 要望が未解析（rulesStale）なら AI で解析してプレビューを出す。未解析でなければ保存済みのルールをそのまま表示
 * 閲覧のみの権限では保存・解析を行わず、保存済みのルールを表示する
 */
const enterRulesStep = async () => {
  if (!canEdit || generating.value || rulesParsing.value) return;
  if (inputDirty.value) {
    if (inputSaving.value) return;
    if (!(await state.saveInput({ silent: true }))) {
      activeTab.value = "input";
      return;
    }
  }
  // 保存中に別のステップへ移った場合は解析しない
  if (activeTab.value !== "rules") return;
  if (rulesStale.value) await state.parseRules();
};

watch(activeTab, (tab) => {
  if (tab === "rules") enterRulesStep();
});

// ②の「編集」：要望を直すために①に戻る
const handleEditRules = () => {
  activeTab.value = "input";
};

// 生成中の経過時間
const elapsed = ref(0);
let elapsedTimer = null;
watch(generating, (running) => {
  clearInterval(elapsedTimer);
  if (!running) return;
  const tick = () => {
    elapsed.value = Math.max(0, Math.floor((Date.now() - genStartedAt.value) / 1000));
  };
  tick();
  elapsedTimer = setInterval(tick, ELAPSED_INTERVAL);
});
const elapsedText = computed(() => `${Math.floor(elapsed.value / 60)}分${String(elapsed.value % 60).padStart(2, "0")}秒`);
// v-loading の element-loading-text 属性は表示開始時にしか読まれないため、
// オブジェクトで渡して経過時間の更新を反映させる（false で非表示）
const genLoading = computed(() => (generating.value
  ? { text: `AIがタイムチャートを作成しています（1〜2分程度）… ${elapsedText.value}` }
  : false));

// 生成が終わったらタイムチャートを表示
watch(generating, (running, wasRunning) => {
  if (wasRunning && !running && !genError.value) {
    activeTab.value = "chart";
    if (!days.value.includes(activeDay.value)) activeDay.value = days.value[0] || "";
  }
});

/**
 * ②の「ルールを確定」：上書き確認 → ルール保存 → AI 生成を開始して③へ
 */
const handleConfirmRules = async () => {
  if (await state.confirmRulesAndGenerate()) {
    activeTab.value = "chart";
    if (!days.value.includes(activeDay.value)) activeDay.value = days.value[0] || "";
  }
};

const handleDiscardShows = async () => {
  try {
    await ElMessageBox.confirm("タイムチャートの編集内容を破棄して、保存済みの状態に戻しますか？", "確認", {
      confirmButtonText: "破棄",
      cancelButtonText: "キャンセル",
      type: "warning",
    });
  } catch {
    return;
  }
  highlightKey.value = "";
  await state.discardShows();
};

// ==================== 上映回ダイアログ ====================

const dialogVisible = ref(false);
const dialogShowKey = ref("");
const dialogPreset = ref({});

const openShow = (key) => {
  dialogShowKey.value = key;
  dialogPreset.value = {};
  dialogVisible.value = true;
};

const addShow = (preset) => {
  dialogShowKey.value = "";
  dialogPreset.value = { bizDate: activeDay.value, ...preset };
  dialogVisible.value = true;
};

/**
 * 違反を選択したら該当日のタブに切り替えて上映回を強調表示
 */
const selectViolation = (v) => {
  const show = v.showKey ? shows.value.find((s) => s._key === v.showKey) : null;
  const day = show?.bizDate || v.bizDate;
  if (day && days.value.includes(day)) activeDay.value = day;
  highlightKey.value = "";
  // 同じ上映回を続けて選んでもスクロール・点滅させるため一度クリアする
  requestAnimationFrame(() => {
    highlightKey.value = show?._key || "";
  });
};

// ==================== 初期化・離脱確認 ====================

/**
 * ブラウザを閉じる・再読み込みするときの確認
 */
const onBeforeUnload = (e) => {
  if (!anyDirty.value) return;
  e.preventDefault();
  e.returnValue = "";
};

onBeforeRouteLeave(() => confirmDiscard("保存されていない変更があります。破棄して移動しますか？"));

onMounted(async () => {
  window.addEventListener("beforeunload", onBeforeUnload);
  // 期間の指定がなければ、直近の金曜日を含む保存済み期間（なければ金曜日から 1 週間）を開く
  await fetchPeriods();
  const period = periodFromQuery(route.query) || defaultPeriod(periods.value);
  await state.changePeriod(period.dateFrom, period.dateTo);
  activeDay.value = days.value[0] || "";
  if (generating.value || shows.value.length) activeTab.value = "chart";
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", onBeforeUnload);
  clearInterval(elapsedTimer);
  state.dispose();
});

const reload = () => state.changePeriod(dateFrom.value, dateTo.value);
</script>

<template>
  <div v-loading="loading" class="plan-page">
    <header class="page-bar">
      <div class="page-bar__title">
        <h2>タイムチャート作成</h2>
        <span class="draft-note">AI案は下書きです。公開されません</span>
      </div>
    </header>

    <!-- ステップと対象期間。期間の選択は①「期間の設定」に置き、②③では表示中の期間を文字だけで出す
         （取得失敗時は①が出ないため、期間を切り替えられるようここに期間選択を出す） -->
    <div v-if="periodKey" class="step-row">
      <nav v-if="!loadErrorMsg" class="stepper" aria-label="作成ステップ">
        <ol>
          <li
            v-for="step in steps"
            :key="step.name"
            class="stepper__item"
            :class="{
              'is-active': activeTab === step.name,
              'is-done': step.done,
              'is-locked': step.locked,
            }"
          >
            <el-tooltip :disabled="!step.locked" :content="STEP1_REQUIRED_MSG" placement="bottom">
              <button
                type="button"
                class="stepper__btn"
                :aria-current="activeTab === step.name ? 'step' : undefined"
                :aria-disabled="step.locked || undefined"
                @click="goStep(step)"
              >
                <span class="stepper__mark" aria-hidden="true">
                  <el-icon v-if="step.locked"><Lock /></el-icon>
                  <el-icon v-else-if="step.done"><Check /></el-icon>
                  <template v-else>{{ step.no }}</template>
                </span>
                <span class="stepper__text">
                  <span class="stepper__title">
                    <span class="sr-only">ステップ{{ step.no }}</span>{{ step.title }}
                  </span>
                  <span class="stepper__note" :class="{ 'is-alert': step.alert }">{{ step.note }}</span>
                </span>
              </button>
            </el-tooltip>
          </li>
        </ol>
      </nav>
      <div v-if="loadErrorMsg" class="period-bar">
        <PeriodPicker :date-from="dateFrom" :date-to="dateTo" :periods="periods" @change="switchPeriod" />
      </div>
      <div v-else-if="activeTab !== 'input'" class="period-text">
        <span class="period-text__label">対象期間</span>
        <span class="period-text__value">{{ periodRangeLabel(dateFrom, dateTo) }}</span>
      </div>
    </div>

    <!-- 取得失敗（ステップ1・2 未登録、権限不足など）。前提ステップが原因のときだけ該当ページへのリンクを出す -->
    <el-result v-if="loadErrorMsg && !loading" icon="warning" :title="loadErrorMsg">
      <template #extra>
        <div class="prereq-links">
          <el-button v-if="settingPath && loadErrorStep === 'setting'" @click="router.push(settingPath)">
            劇場・スクリーン設定へ
          </el-button>
          <el-button v-if="moviePath && loadErrorStep === 'movie'" @click="router.push(moviePath)">
            本館上映作品・バージョン登録へ
          </el-button>
          <el-button type="primary" @click="reload">再読み込み</el-button>
        </div>
      </template>
    </el-result>

    <template v-else-if="periodKey && !loading">
      <!-- 保存済みの期間と重なっている（重なる日の上映回は AI 生成・保存時に確認のうえ上書きされる） -->
      <el-alert
        v-if="overlaps.length"
        :type="splitOverlaps.length ? 'error' : 'warning'"
        :closable="false"
        show-icon
        class="overlap-alert"
      >
        <template #title>{{ overlapMessage }}</template>
        <div class="overlap-alert__list">
          <el-button
            v-for="o in overlaps"
            :key="`${o.dateFrom}~${o.dateTo}`"
            size="small"
            @click="switchPeriod(o)"
          >
            {{ periodRangeLabel(o.dateFrom, o.dateTo) }} を開く
          </el-button>
        </div>
      </el-alert>

      <!-- 常に表示する状態バー -->
      <div class="status-bar" :class="`is-${(compliance || 'none').toLowerCase()}`" role="status">
        <div class="status-bar__main">
          <span v-if="compliance === 'COMPLIANT'" class="status-badge status-badge--ok">✓ 実行可能</span>
          <span v-else-if="compliance === 'NON_COMPLIANT'" class="status-badge status-badge--ng">
            ⚠ 未満足の必須条件あり（非準拠の下書き）
          </span>
          <!-- 配給会社の必須外の要望で判定できなかったもの（クリックで一覧） -->
          <el-popover
            v-if="compliance && distributorPending.length"
            placement="bottom-start"
            :width="380"
            trigger="click"
            popper-class="pending-popper"
          >
            <template #reference>
              <el-button type="warning" size="small" plain>要確認 {{ distributorPending.length }}件</el-button>
            </template>
            <p class="pending-popper__note">
              配給会社の要望のうち、必須条件として扱われず判定できなかった項目です。タイムチャートに反映されているか確認してください
            </p>
            <ul class="pending-popper__list">
              <li v-for="p in distributorPending" :key="p.key">
                <div class="pending-popper__title">{{ p.title }}</div>
                <p v-if="p.reason" class="pending-popper__reason">{{ p.reason }}</p>
              </li>
            </ul>
          </el-popover>
          <!-- 上映回が空のときは合否を出さない（保存済み・編集中に全削除した場合は「上映回がありません」） -->
          <span v-else class="status-badge">
            {{ hasSavedShows || showsDirty ? "上映回がありません" : "タイムチャート未作成" }}
          </span>
          <span v-if="shows.length || showsDirty" class="status-meta">
            <template v-if="shows.length">上映回 {{ shows.length }}</template>
            <template v-if="violations.length">／ <span class="danger-text">違反 {{ violations.length }}</span></template>
            <template v-if="showsDirty">{{ shows.length ? "／ " : "" }}<span class="warn-text">未保存の編集あり</span></template>
          </span>
        </div>
        <div v-if="canEdit" class="status-bar__actions">
          <!-- 保存済みの期間を削除（①「期間の設定」と同じ処理。どのステップからでも削除できるよう状態バーにも置く） -->
          <el-button
            v-if="saved"
            v-hasPermi="['dp:scheduleAi:plan:edit']"
            type="danger"
            plain
            :loading="deleting"
            :disabled="generating"
            @click="handleDeletePeriod"
          >
            この期間を削除
          </el-button>
          <el-button
            v-hasPermi="['dp:scheduleAi:plan:edit']"
            :disabled="!showsDirty || generating"
            @click="handleDiscardShows"
          >
            編集を破棄
          </el-button>
          <el-button
            v-hasPermi="['dp:scheduleAi:plan:edit']"
            type="primary"
            plain
            :loading="showsSaving"
            :disabled="!showsDirty || generating"
            @click="state.saveShows()"
          >
            タイムチャートを保存
          </el-button>
        </div>
      </div>

      <!-- AI 生成の失敗 -->
      <el-alert v-if="genError" type="error" :closable="false" show-icon class="gen-error">
        <template #title>{{ genError }}</template>
        <div class="gen-error__actions">
          <span class="muted">保存済みのタイムチャートは変更されていません</span>
          <el-button v-if="settingPath && genErrorStep === 'setting'" link type="primary" @click="router.push(settingPath)">
            劇場・スクリーン設定
          </el-button>
          <el-button v-if="moviePath && genErrorStep === 'movie'" link type="primary" @click="router.push(moviePath)">
            上映作品・バージョン登録
          </el-button>
          <!-- 再生成は②の「ルールを確定」から行う -->
          <el-button v-hasPermi="['dp:scheduleAi:plan:edit']" size="small" type="danger" plain @click="activeTab = 'rules'">
            ルール確認に戻る
          </el-button>
        </div>
      </el-alert>

      <!-- 各ステップの内容（切り替えても入力状態を残すため v-show） -->
      <div v-show="activeTab === 'input'" class="step-pane">
        <WeekInput>
          <!-- 対象期間の選択（日付範囲・保存済みの期間・この期間を削除） -->
          <template #period>
            <div class="period-bar">
              <PeriodPicker :date-from="dateFrom" :date-to="dateTo" :periods="periods" @change="switchPeriod" />
              <el-button
                v-if="canEdit && saved"
                v-hasPermi="['dp:scheduleAi:plan:edit']"
                type="danger"
                text
                :loading="deleting"
                :disabled="generating"
                @click="handleDeletePeriod"
              >
                この期間を削除
              </el-button>
            </div>
          </template>
        </WeekInput>
      </div>
      <div v-show="activeTab === 'rules'" class="step-pane">
        <RulePanel @edit="handleEditRules" @confirm="handleConfirmRules" />
      </div>
      <div v-show="activeTab === 'chart'" class="step-pane">
        <div class="chart-layout">
          <!-- 左：タイムチャートとその下に AI の説明 / 右：ルール照合・違反 -->
          <div class="chart-main">
            <section v-loading="genLoading" class="panel chart-panel">
              <el-empty
                v-if="!shows.length && !generating && !showsDirty"
                :image-size="72"
                :description="movies.length ? '「ルール確認」で「ルールを確定」するとAIがタイムチャートを作成します。上映回を手動で追加することもできます' : '上映作品が登録されていません'"
              >
                <el-button
                  v-if="canEdit && movies.length"
                  v-hasPermi="['dp:scheduleAi:plan:edit']"
                  @click="addShow({})"
                >
                  上映回を追加
                </el-button>
              </el-empty>
              <TimeChart
                v-else
                v-model:active-day="activeDay"
                :highlight-key="highlightKey"
                @open-show="openShow"
                @add-show="addShow"
              />
            </section>
            <CheckPanel section="summary" />
          </div>
          <CheckPanel section="check" class="side-panel" @select-violation="selectViolation" />
        </div>
      </div>
    </template>

    <ShowDialog v-model="dialogVisible" :show-key="dialogShowKey" :preset="dialogPreset" />
  </div>
</template>

<style lang="less">
/* 子コンポーネントでも使う共通トークン・パネル（ステップ1・2 と同じデザイン） */
.plan-page {
  --sp-brand: var(--brand-color, #cc251b);
  --sp-panel: var(--bg-panel, #fff);
  --sp-border: var(--border-color, #e5e5e5);
  --sp-text: var(--text-primary, #000);
  --sp-text-2: var(--text-secondary, #4a4a4a);
  --sp-muted: var(--text-tertiary, #757575);
  --sp-tint: color-mix(in srgb, var(--sp-brand) 7%, var(--sp-panel));
  --sp-tint-strong: color-mix(in srgb, var(--sp-brand) 14%, var(--sp-panel));
  --sp-radius: var(--border-radius, 6px);

  .panel {
    margin-bottom: 16px;
    border: 1px solid var(--sp-border);
    border-radius: var(--sp-radius);
    background: var(--sp-panel);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
  }

  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 18px;
    border-bottom: 1px solid var(--sp-border);
    background: var(--sp-tint);

    h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 700;
    }
  }

  .panel-count {
    margin-left: 8px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: var(--sp-muted);
  }

  .panel-note {
    font-size: 12px;
    color: var(--sp-muted);
  }

  .hint {
    width: 100%;
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.6;
    color: var(--sp-muted);
  }

  .muted {
    color: var(--sp-muted);
  }

  .warn-text {
    font-size: 12px;
    color: var(--el-color-warning);
  }

  .danger-text {
    font-weight: 700;
    color: var(--el-color-danger);
  }
}

/* 状態バー「要確認」の一覧（body 直下に表示されるため .plan-page の外に置く） */
.pending-popper {
  &__note {
    margin: 0 0 8px;
    font-size: 12px;
    line-height: 1.6;
    color: var(--text-secondary, #4a4a4a);
  }

  &__list {
    max-height: 280px;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;

    li + li {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--border-color, #e5e5e5);
    }
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
  }

  &__reason {
    margin: 2px 0 0;
    font-size: 12px;
    color: var(--text-tertiary, #757575);
  }
}
</style>

<style lang="less" scoped>
.plan-page {
  min-height: 200px;
  padding: 0 24px 40px 15px;
  box-sizing: border-box;
  color: var(--sp-text);
}

/* ---------- 操作バー ---------- */
.page-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  padding: 15px 0 12px;

  &__title {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 12px;

    h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
    }
  }
}

.draft-note {
  padding: 2px 8px;
  border: 1px solid var(--sp-border);
  border-radius: 4px;
  font-size: 12px;
  color: var(--sp-text-2);
}

/* ---------- ステップ・対象期間 ---------- */
.step-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 24px;
  margin-bottom: 12px;
}

.stepper {
  flex: 1 1 520px;
  min-width: 0;

  ol {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__item {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;

    // ステップ間の連結線（前のステップが完了していれば色付き）
    & + &::before {
      content: "";
      flex: 1;
      min-width: 16px;
      height: 2px;
      margin: 0 10px;
      border-radius: 1px;
      background: var(--sp-border);
    }

    &.is-done + &::before {
      background: var(--el-color-success);
    }

    // 最初のステップは連結線がないので伸ばさない
    &:first-child {
      flex: none;
    }
  }

  &__btn {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 6px 10px 6px 6px;
    border: 1px solid transparent;
    border-radius: 999px;
    font: inherit;
    text-align: left;
    color: var(--sp-text-2);
    background: transparent;
    cursor: pointer;

    &:hover {
      background: var(--sp-tint);
    }

    &:focus-visible {
      outline: 2px solid var(--sp-brand);
      outline-offset: 1px;
    }
  }

  &__mark {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 2px solid var(--sp-border);
    border-radius: 50%;
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--sp-muted);
    background: var(--sp-panel);
    box-sizing: border-box;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
  }

  &__note {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: var(--sp-muted);

    &.is-alert {
      font-weight: 600;
      color: var(--el-color-danger);
    }
  }

  // 完了：緑のチェック
  .is-done &__mark {
    border-color: var(--el-color-success);
    color: #fff;
    background: var(--el-color-success);
  }

  // 表示中：ブランド色で囲み、背景も付ける
  .is-active &__btn {
    border-color: color-mix(in srgb, var(--sp-brand) 45%, var(--sp-border));
    color: var(--sp-text);
    background: var(--sp-tint-strong);
  }

  .is-active:not(.is-done) &__mark {
    border-color: var(--sp-brand);
    color: var(--sp-text);
    background: var(--sp-brand);
  }

  // ①未保存で進めない：薄く表示
  .is-locked &__btn {
    cursor: not-allowed;
    opacity: 0.55;

    &:hover {
      background: transparent;
    }
  }
}

// スクリーンリーダー向けの補足（画面には出さない）
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.period-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
}

// ②③での対象期間の表示（読み取り専用）
.period-text {
  display: flex;
  align-items: baseline;
  gap: 8px;
  white-space: nowrap;

  &__label {
    font-size: 12px;
    color: var(--sp-muted);
  }

  &__value {
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.overlap-alert {
  margin-bottom: 8px;

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;

    .el-button + .el-button {
      margin-left: 0;
    }
  }
}

.prereq-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

/* ---------- 状態バー ---------- */
.status-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-bottom: 8px;
  padding: 10px 16px;
  border: 1px solid var(--sp-border);
  border-left-width: 5px;
  border-radius: var(--sp-radius);
  background: var(--sp-panel);

  &.is-compliant {
    border-left-color: var(--el-color-success);
  }

  &.is-non_compliant {
    border-color: var(--el-color-danger);
    background: color-mix(in srgb, var(--el-color-danger) 8%, var(--sp-panel));
  }

  &__main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 16px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    .el-button + .el-button {
      margin-left: 0;
    }
  }
}

.status-badge {
  font-size: 14px;
  font-weight: 700;
  color: var(--sp-text-2);

  &--ok {
    color: var(--el-color-success);
  }

  &--ng {
    font-size: 15px;
    color: var(--el-color-danger);
  }
}

.status-meta {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--sp-text-2);
}

.gen-error {
  margin-bottom: 8px;

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin-top: 6px;
  }
}

/* ---------- ステップの内容 ---------- */
.step-pane {
  margin-top: 4px;
}

.chart-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;
}

.chart-main {
  min-width: 0;
}

.chart-panel {
  min-height: 240px;
}

@media (max-width: 1280px) {
  .chart-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 768px) {
  .plan-page {
    padding: 0 12px 32px;
  }

  // スマホではステップ名の下の状態表示を省き、番号とステップ名だけにする
  .stepper {
    flex-basis: 100%;

    &__note {
      display: none;
    }

    &__item + &__item::before {
      margin: 0 4px;
    }
  }
}
</style>
